'use client';

import { IMyAccountLogin } from '@/.generated';
import { mobileHeaderVariants } from '@/components/authorable/site-structure/Header/Header.styles';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { getCSLPAttributes } from '@/utils/type-guards';
import Link from 'next/link';
import { useAccountLogin } from '@/lib/hooks/useAccountLogin';
import { trackAdobeClick } from '@/utils/adobe/click';
import { buttonVariants } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper.styles';
import { MY_ACCOUNT_LOGIN_URL } from '@/utils/Constants';

type MobileLoginSectionProps = {
  myAccountLogin?: IMyAccountLogin;
  showAccountLogin?: boolean;
};

const {
  loginWrapper,
  loginForm,
  loginInput,
  loginSubmitButton,
  logoutButton,
  loginFooter,
  loginForgotPasswordLink,
} = mobileHeaderVariants();

export const MobileLoginSection = ({
  showAccountLogin,
  myAccountLogin,
}: MobileLoginSectionProps) => {
  const userLoggedIn = useAccountLogin();

  const loginButtonBase = buttonVariants({
    variant: myAccountLogin?.login_button?.cta_variant ?? 'primary',
    size: 'variable',
    class: loginSubmitButton(),
  });

  if (!showAccountLogin || !myAccountLogin) return null;

  const {
    username_field_placeholder,
    password_field_placeholder,
    login_button,
    disclaimer_note,
    mobile_sign_up_content,
    forgot_password_link,
    mobile_logged_in_content,
    logout_link,
    $,
  } = myAccountLogin;

  const login_link = login_button?.login_link;

  return (
    <div className={loginWrapper()}>
      {!userLoggedIn && (
        <>
          <form
            data-key="loginForm"
            id="mobile-menu-login"
            action={login_link?.href}
            method="post"
            className={loginForm()}
            onSubmit={() =>
              trackAdobeClick({
                clickName: login_link?.title ?? 'Login',
                clickType: 'button',
                clickLocation: 'mobileLogin',
                clickUrl: MY_ACCOUNT_LOGIN_URL,
              })
            }
          >
            <input
              type="text"
              id="txtUsernameMobile"
              name="UserName"
              placeholder={username_field_placeholder}
              autoComplete="username"
              className={loginInput()}
              {...getCSLPAttributes($?.username_field_placeholder)}
            />
            <input
              type="password"
              id="txtPasswordMobile"
              name="Password"
              placeholder={password_field_placeholder}
              autoComplete="current-password"
              className={loginInput()}
              {...getCSLPAttributes($?.password_field_placeholder)}
            />
            <input
              type="submit"
              id="submitMobile"
              value={login_link?.title}
              className={loginButtonBase}
              {...getCSLPAttributes(login_button?.$?.login_link)}
            />
          </form>
          <RichTextWrapper
            content={disclaimer_note}
            className="headerMyLoginDisclaimerRTE"
            cslpAttribute={$?.disclaimer_note}
          />
          <div className={loginFooter()}>
            <RichTextWrapper
              content={mobile_sign_up_content}
              className="headerMyLoginSignupRTE"
              cslpAttribute={$?.mobile_sign_up_content}
            />
            <Link
              href={forgot_password_link?.href || '#'}
              className={loginForgotPasswordLink()}
              onClick={() =>
                trackAdobeClick({
                  clickName: forgot_password_link?.title ?? 'Forgot Password',
                  clickType: 'link',
                  clickLocation: 'mobileLogin',
                })
              }
              {...getCSLPAttributes($?.forgot_password_link)}
            >
              {forgot_password_link?.title}
            </Link>
          </div>
        </>
      )}
      {userLoggedIn && (
        <>
          <RichTextWrapper
            content={mobile_logged_in_content}
            cslpAttribute={$?.mobile_logged_in_content}
            parentClassName="headerLoggedInContent"
          />
          <Link
            href={logout_link?.href || ''}
            className={logoutButton()}
            onClick={() =>
              trackAdobeClick({
                clickName: logout_link?.title ?? 'Logout',
                clickType: 'link',
                clickLocation: 'mobileLogin',
              })
            }
            {...getCSLPAttributes($?.logout_link)}
          >
            {logout_link?.title}
          </Link>
        </>
      )}
    </div>
  );
};
